import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, Calculator, ReceiptText, FileDown, MousePointerClick, Layers, ArrowRight, Sparkles, Users, CreditCard, Landmark, Plug, Wand2, ScanLine, Copy, Camera, MessageSquareText } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import PricingCards from "@/components/PricingCards";
import Seo from "@/components/Seo";
import HeroCarousel from "@/components/HeroCarousel";
import AccountingSync from "@/components/AccountingSync";
import ReviewBadges from "@/components/ReviewBadges";
import RoiCalculator from "@/components/RoiCalculator";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import api from "@/lib/api";
import { FEATURE_BLUEPRINT } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Layers, title: "Upload & render blueprints", desc: "Drop a PDF drawing and view it right in the browser. No plugins, no CAD licence." },
  { icon: Ruler, title: "Calibrate the scale", desc: "Set your drawing scale in seconds so every measurement comes out accurate." },
  { icon: MousePointerClick, title: "Trace & count takeoff", desc: "Click to count fittings, trace runs to measure lengths, and aggregate quantities per job." },
  { icon: Sparkles, title: "AI takeoff assistant", desc: "Let AI read schedules & legends to extract quantities, detect the drawing scale, and suggest rates from your library — you stay in control." },
  { icon: Calculator, title: "Estimating matrix", desc: "A fast spreadsheet-style grid. Quantity, unit, rate, markup and live totals." },
  { icon: ReceiptText, title: "Progress billing ledger", desc: "Schedule-of-values billing with retainage, balance to finish and amount due." },
  { icon: Landmark, title: "UK tax built in — CIS & VAT", desc: "Automatic CIS deductions on labour and VAT domestic reverse charge, right on your Applications for Payment." },
  { icon: CreditCard, title: "Get paid by card or BACS", desc: "Share an Application for Payment and let clients pay by card via Stripe — straight to your bank — or by bank transfer." },
  { icon: Users, title: "Work as a team", desc: "Invite your estimators, QSs and admins with owner, admin, member and read-only viewer roles." },
  { icon: Plug, title: "Xero accounting sync", desc: "Send any valuation to Xero as a draft invoice in one click — client contacts synced, CIS & reverse-charge VAT handled, with live Draft/Approved/Paid status. Auto-send on approval too." },
  { icon: FileDown, title: "One-click PDF export", desc: "Send a clean, branded estimate and application for payment to your client." },
];

const HIGHLIGHTS = [
  "AI takeoff assistant",
  "Blueprint takeoff in the browser",
  "Estimating + progress billing",
  "CIS & VAT reverse charge",
  "Card & BACS payments",
  "Xero accounting sync",
  "Team roles & seats",
];

const AI_CAPS = [
  { icon: Wand2, title: "Detect the drawing scale", desc: "AI reads the title block or scale bar and tells you the scale, so you calibrate in seconds." },
  { icon: Sparkles, title: "Read schedules & legends", desc: "Pull door, window, sanitaryware and fitting quantities straight off schedule tables into your estimate." },
  { icon: Copy, title: "Auto-count symbols", desc: "Box one socket, door or light and TradeOff finds every matching symbol on the sheet." },
  { icon: ScanLine, title: "Detect measurements", desc: "Scan a region of a PDF and TradeOff reads the dimensions and measured lines for you." },
  { icon: Calculator, title: "Suggest rates", desc: "Price extracted quantities with suggested rates from your own library — you always stay in control." },
  { icon: MessageSquareText, title: "Ask the drawing", desc: "Ask questions in plain English — \"how many fire doors?\", \"what's the floor area?\" — and get an answer with its source, on desktop or your phone on site." },
];

const STEPS = [
  { n: "01", t: "Create a project", d: "Spin up a job and invite the drawing." },
  { n: "02", t: "Take off quantities", d: "Calibrate, count and trace on the plan." },
  { n: "03", t: "Estimate & bill", d: "Price the work, then bill progress each month." },
  { n: "04", t: "Export & win", d: "Export a professional PDF and get paid." },
];

const FAQS = [
  { q: "What is TradeOff?", a: "TradeOff is takeoff, estimating and progress billing software built for UK subcontractors. Upload a PDF or scanned blueprint, take off quantities (with AI help), price the job from your own rate library, then run progress billing with CIS and VAT reverse charge handled automatically." },
  { q: "Does TradeOff use AI for takeoff?", a: "Yes — TradeOff has a built-in AI takeoff assistant. Hit 'Run AI takeoff' and it reads your PDF drawing to detect the scale and pull quantities from schedules and legends in a single pass. You can also auto-count repeated symbols, detect measurements in a region, and get suggested rates from your own library. The AI does the grunt work; you review every number and stay in control." },
  { q: "How do I do a takeoff from a PDF drawing?", a: "Upload a PDF or image blueprint, calibrate the scale once, then click to count fittings and trace runs to measure lengths. TradeOff can also auto-count repeated symbols and detect measurements for you, so a takeoff that used to take hours takes minutes." },
  { q: "How does TradeOff handle CIS deductions on an application for payment?", a: "TradeOff applies the Construction Industry Scheme (CIS) deduction to the labour element of your billing automatically — at 0%, 20% or 30% — after retention, and shows a clear breakdown on the ledger and the PDF Application for Payment." },
  { q: "Does it support VAT domestic reverse charge?", a: "Yes. You can set VAT treatment to domestic reverse charge and TradeOff shows the correct £0 VAT line plus the required HMRC reverse-charge statement on your applications and PDFs." },
  { q: "Can my client pay online?", a: "Yes. Share a branded Application for Payment as a secure link — your client can view it, download a PDF, approve it and pay by card (via Stripe, straight to your bank) or by BACS bank transfer." },
  { q: "Does TradeOff integrate with Xero?", a: "Yes. Connect your Xero organisation and send any valuation to Xero as a draft sales invoice in one click — TradeOff syncs the client contact, lets Xero calculate CIS and applies domestic reverse-charge VAT, then shows live Draft/Approved/Paid status back in TradeOff. You can also auto-send to Xero whenever a client approves an Application for Payment. Prefer a different package? Export a Xero/QuickBooks/Sage-ready invoice CSV instead." },
  { q: "How much does TradeOff cost?", a: "TradeOff has simple monthly pricing in GBP with a free plan to get started — no credit card required, and you can set up and take off your first drawing in about a minute." },
];

const SITE_URL = "https://tradeoff.blueshuckconsulting.com";
const STRUCTURED_DATA = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "TradeOff",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "AI Construction Takeoff & Estimating Software",
    operatingSystem: "Web",
    url: SITE_URL,
    description: "AI takeoff, estimating and progress billing software for UK subcontractors. AI reads your drawings to detect the scale, extract quantities from schedules and legends and count symbols, with automatic CIS & VAT reverse charge.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "GBP", description: "Free plan — no credit card required" },
    publisher: { "@type": "Organization", name: "Blue Shuck Consulting Ltd", url: "https://www.blueshuckconsulting.com" },
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Blue Shuck Consulting Ltd",
    url: "https://www.blueshuckconsulting.com",
    brand: "TradeOff",
    areaServed: "GB",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

const Landing = () => {
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const choosePlan = (key) => {
    if (key === "free") return navigate(user ? "/app" : "/register");
    navigate(`/register?plan=${key}`);
  };

  return (
    <div className="bg-white">
      <Seo title="AI Takeoff & Estimating Software for UK Subcontractors" description="TradeOff is AI takeoff, estimating and progress billing software for UK subcontractors. AI reads your PDF drawings to detect the scale, extract quantities from schedules and count symbols — then price and bill every job. Start free, no card." path="/" jsonLd={STRUCTURED_DATA} />
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">AI takeoff · estimating · progress billing</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              <span className="text-blue-600">AI takeoff</span> for UK subcontractors — measure, price and bill every job.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl">
              TradeOff reads your PDF drawings like a quantity surveyor — <span className="font-semibold text-slate-800">detecting the scale, pulling quantities from schedules and legends and counting symbols</span> in seconds — then takes you from takeoff to a priced estimate and progress billing, all in one place.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => navigate("/register")} data-testid="hero-start-btn" className="bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">
                Start free — 1 project <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} data-testid="hero-pricing-btn" className="h-12 px-7 text-base border-slate-300">
                See pricing
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-500">No credit card required · Free 1 project trial · Set up in 60 seconds</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
            <HeroCarousel />
          </motion.div>
        </div>
      </section>

      {/* AI takeoff — message-match for AI-intent search */}
      <section id="ai" className="py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Built-in AI</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              AI takeoff that does the grunt work
            </h2>
            <p className="mt-4 text-slate-600">
              Hit <span className="font-semibold text-slate-800">Run AI takeoff</span> and TradeOff reads your drawing like a QS — detecting the scale and pulling quantities off schedules and legends in one pass. Review, tweak and drop straight into your estimate. You always stay in control.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {AI_CAPS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-xl border border-blue-100 bg-blue-50/40 p-6"
                data-testid={`ai-cap-${i}`}
              >
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10">
            <Button size="lg" onClick={() => navigate("/register")} data-testid="ai-cta-btn" className="bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">
              Try AI takeoff free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="mt-3 text-sm text-slate-500">A faster, lower-cost alternative to traditional takeoff tools — no CAD licence, works in your browser.</p>
          </div>
        </div>
      </section>

      {/* Snap-a-photo (mobile) */}
      <section id="snap" className="py-24 bg-slate-50 border-b border-slate-200" data-testid="landing-snap">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">On your phone</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Snap a drawing, get a priced estimate
            </h2>
            <p className="mt-4 text-slate-600">
              On site with just your phone? Photograph a drawing's schedule or legend and TradeOff's AI reads the quantities and prices them from your rate library — an estimate before you're back in the van.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-3 gap-5">
            {[
              { icon: Camera, n: "01", t: "Snap a photo", d: "Take a photo of the schedule, legend or drawing straight from your phone." },
              { icon: Sparkles, n: "02", t: "AI reads it", d: "TradeOff extracts the quantities and detects the drawing scale automatically." },
              { icon: Calculator, n: "03", t: "Priced estimate", d: "Prices drop in from your rate library — review, tweak and send." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative bg-white border border-slate-200 rounded-xl p-6"
                data-testid={`snap-step-${i}`}
              >
                <span className="absolute top-4 right-5 font-mono text-sm font-bold text-slate-200">{s.n}</span>
                <div className="h-11 w-11 rounded-lg bg-blue-600/10 flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10">
            <Button size="lg" onClick={() => navigate("/register")} data-testid="snap-cta-btn" className="bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">
              Try it on your phone <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ROI calculator */}
      <section id="roi" className="py-24 bg-white border-b border-slate-200" data-testid="landing-roi">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">See what it's worth to you</h2>
            <p className="mt-3 text-slate-600">Every hour off a takeoff is an hour back on the tools — or another bid out the door. Put your own numbers in.</p>
          </div>
          <RoiCalculator />
        </div>
      </section>

      {/* Social proof / reviews */}
      <section className="border-y border-slate-200 bg-navy" style={{ backgroundColor: "#16305C" }} data-testid="landing-reviews">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <p className="text-slate-200 text-sm font-semibold mb-6">Don't just take our word for it — see what subcontractors say.</p>
          <ReviewBadges />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Everything, one workflow</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              The unified takeoff-to-billing toolkit
            </h2>
            <p className="mt-4 text-slate-600">Every plan gets every feature. The only thing that changes is how many jobs and teammates you run at once.</p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="landing-highlights">
              {HIGHLIGHTS.map((h) => (
                <span key={h} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-full px-3 py-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> {h}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow"
                data-testid={`feature-card-${i}`}
              >
                <div className="h-11 w-11 rounded-lg bg-blue-50 flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Accounting sync (Xero live; QuickBooks & Sage coming) */}
      <AccountingSync />

      {/* Workflow */}
      <section id="workflow" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
            <img src={FEATURE_BLUEPRINT} alt="Estimating on blueprint" className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">How it works</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">From drawing to paid, in four steps</h2>
            <div className="mt-8 space-y-6">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-4" data-testid={`workflow-step-${s.n}`}>
                  <span className="font-mono text-blue-600 font-bold text-lg">{s.n}</span>
                  <div>
                    <h4 className="font-display font-semibold text-slate-900">{s.t}</h4>
                    <p className="text-sm text-slate-600">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Simple, honest pricing</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Pay for active projects, nothing else</h2>
            <p className="mt-4 text-slate-600">All features on every plan. Archive a finished job to free up space, or upgrade when you need more running at once.</p>
          </div>
          <div className="mt-12">
            <PricingCards plans={plans} onSelect={choosePlan} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="faq" className="py-24 bg-slate-50 border-y border-slate-200" data-testid="landing-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 text-center">Frequently asked questions</h2>
          <p className="mt-3 text-slate-500 text-center">Takeoff, estimating and UK-ready progress billing — answered.</p>
          <Accordion type="single" collapsible className="mt-10">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold text-slate-800">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-[15px] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20" style={{ backgroundColor: "#16305C" }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">Win the job. Bill it right. Get paid.</h2>
          <p className="mt-4 text-slate-300">Start free today — no card, no commitment.</p>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="cta-start-btn" className="mt-8 bg-blue-600 hover:bg-blue-500 h-12 px-8 text-base">
            Create your workspace <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
